import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FilterMatchMode } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Sidebar } from 'primereact/sidebar';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import CommonConfig from '../../utils/constant';
import Toast from '../Shared/Toast/Toast';
import DeleteConfirmation from '../Dialogs/DeleteConfirmation';
import { useCatchResponse } from '../../utils/useCatchResponse';
import AddEditLMS from './AddEditLMS';
import LMSSubmissions from './LMSSubmissions';

const typeLabels = {
    Syllabus: 'Syllabus',
    Resources: 'Resources',
    QuestionPaper: 'Question Paper',
    Assignments: 'Assignments',
};
const typeColors = { Syllabus: '#14452f', Resources: '#2f6fed', QuestionPaper: '#B1007E', Assignments: '#F5A623' };

const LMS = () => {
    const { catchResponse } = useCatchResponse();
    const [list, setList] = useState([]);
    const [masterData, setMasterData] = useState(null);
    const [visible, setVisible] = useState(false);
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [securityUserID, setSecurityUserID] = useState('');
    const [schoolId, setSchoolId] = useState('');
    const [financialYear, setFinancialYear] = useState('');
    const [rows, setRows] = useState(10);
    const [first, setFirst] = useState(0);
    const [isDeleteConfirmation, setIsDeleteConfirmation] = useState(false);
    const [deleteId, setDeleteId] = useState('');
    const [pageAccess, setPageAccess] = useState({});
    const [classMap, setClassMap] = useState({});
    const [termMap, setTermMap] = useState({});
    const [subjectMap, setSubjectMap] = useState({});
    const [submissionsRow, setSubmissionsRow] = useState(null);

    useEffect(() => {
        const init = async () => {
            const loginData = CommonConfig.loginData();
            const userId = loginData?._id;
            const schoolid = CommonConfig.isOrgOwner(loginData?.UserRole) ? userId : loginData?.CreatedBy;
            setSecurityUserID(userId);
            setSchoolId(schoolid);

            const accessData = CommonConfig.getUserAccess('LMS');
            setPageAccess(accessData);

            const years = await CommonConfig.getFinancialYears(schoolid);
            const currentYear = CommonConfig.getCurrentFinancialYear(years);
            const fy = CommonConfig.getCookie('selectedAcademicYear') ?? currentYear?._id ?? currentYear?.value;
            setFinancialYear(fy);

            await loadLabelMaps(userId, schoolid, fy);
            getList(schoolid, fy);
        };
        init();
    }, []);

    const loadLabelMaps = async (userId, schoolid, fy) => {
        try {
            const classRes = await api.post(APIConstant.path.getClassList, { UserID: schoolid, FinancialYearId: fy });
            if (classRes.success) {
                const cm = {};
                (classRes.data || []).forEach(x => { cm[x._id] = `${x?.ClassName}-${x?.SectionFullName}-${x?.MediumName}`; });
                setClassMap(cm);
            }
        } catch (err) { console.log(err); }

        try {
            const termRes = await api.post(APIConstant.path.getTermList, { UserID: schoolid });
            if (termRes.success) {
                const tm = {};
                (termRes.data || []).forEach(x => { tm[x._id] = x?.label || x?.TermName; });
                setTermMap(tm);
            }
        } catch (err) { console.log(err); }

        try {
            const subjectRes = await api.post(APIConstant.path.getSubjectList, { UserID: schoolid });
            if (subjectRes.success) {
                const sm = {};
                (subjectRes.data || []).forEach(x => { sm[x._id] = x?.SubjectName; });
                setSubjectMap(sm);
            }
        } catch (err) { console.log(err); }
    };

    const getList = async (sid, fy) => {
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.GetLMSList, { SchoolId: sid || schoolId, FinancialYearId: fy || financialYear, UserId: CommonConfig.loginData()?._id });
            if (response.success) setList(response.data || []);
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const onGlobalFilterChange = (e) => {
        let _filters = { ...filters };
        _filters['global'].value = e.target.value;
        setFilters(_filters);
        setGlobalFilterValue(e.target.value);
    };

    const onCancel = () => { setVisible(false); setMasterData(null); getList(schoolId, financialYear); };

    const handleDelete = async () => {
        try {
            const response = await api.post(APIConstant.path.DeleteLMS, { id: deleteId });
            if (response.success) { Toast.success({ message: 'Deleted successfully' }); getList(schoolId, financialYear); }
            setIsDeleteConfirmation(false); setDeleteId('');
        } catch (err) { setIsDeleteConfirmation(false); catchResponse(err); }
    };

    const srNoTemplate = (_, { rowIndex }) => rowIndex + 1;

    const mapNames = (ids, map) => (ids || []).map(id => map[id]).filter(Boolean).join(', ') || '-';

    const typeTemplate = (row) => <Tag value={typeLabels[row.Type] || row.Type} style={{ background: typeColors[row.Type] || '#666' }} />;
    const classTemplate = (row) => <span>{mapNames(row.Class, classMap)}</span>;
    const subjectTemplate = (row) => <span>{mapNames(row.Subject, subjectMap)}</span>;
    const semesterTemplate = (row) => <span>{mapNames(row.Term, termMap)}</span>;
    const notifyTemplate = (row) => row.Notify
        ? <Tag value="On" severity="success" />
        : <Tag value="Off" severity="danger" />;
    const attachmentTemplate = (row) => <span>{row.Attachments?.length || 0}</span>;
    const createdByTemplate = (row) => (
        row.IsBySchool
            ? <Tag value={row.CreatedByName || 'School'} style={{ background: '#14452f' }} />
            : <Tag value={row.CreatedByName || 'Teacher'} severity="info" />
    );

    const actionTemplate = (row) => (
        <div style={{ display: 'flex', gap: '4px' }}>
            {row.Type === 'Assignments' && (
                <Button icon="pi pi-eye" className="e-sction-btn p-m-0" tooltip="View Submissions" tooltipOptions={{ position: 'top' }}
                    onClick={() => setSubmissionsRow(row)} />
            )}
            {pageAccess?.EditAccess && (
                <Button icon="pi pi-pencil" className="e-sction-btn p-m-0"
                    onClick={() => { setMasterData(row); setVisible(true); }} />
            )}
            {pageAccess?.DeleteAccess && (
                <Button icon="pi pi-trash" className="e-sction-btn p-m-0"
                    onClick={() => { setDeleteId(row._id); setIsDeleteConfirmation(true); }} />
            )}
        </div>
    );

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-book"></i>
                    <h2>Learning Management System</h2>
                </div>
                <div className="p-d-flex p-ai-center">
                    <div className="searchbar-area e-searchbar-area">
                        <i className="pi pi-search" />
                        <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Search" />
                    </div>
                    {pageAccess?.AddAccess && <Button icon="pi pi-plus" className="e-h-btn" onClick={() => setVisible(true)} />}
                </div>
            </div>

            <DataTable value={list} paginator rows={rows} first={first} filters={filters} filterDisplay="menu"
                globalFilterFields={['Name', 'Type', 'Remarks']}
                responsiveLayout="scroll" paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                rowsPerPageOptions={[10, 20, 50]} currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                onPage={(e) => { setFirst(e.first); setRows(e.rows); }} className='custom-table' scrollHeight='calc(100vh - 120px)' size='small'>
                <Column body={srNoTemplate} header="Sr." style={{ width: '5%' }} />
                <Column field="Name" header="Name" style={{ width: '18%' }} />
                <Column body={typeTemplate} header="Type" style={{ width: '10%' }} />
                <Column body={classTemplate} header="Class/Course" style={{ width: '15%' }} />
                <Column body={subjectTemplate} header="Subject" style={{ width: '14%' }} />
                <Column body={semesterTemplate} header="Semester" style={{ width: '12%' }} />
                <Column body={attachmentTemplate} header="Files" style={{ width: '6%' }} />
                <Column body={createdByTemplate} header="Created By" style={{ width: '11%' }} />
                <Column body={notifyTemplate} header="Notify" style={{ width: '7%' }} />
                <Column body={actionTemplate} header="Action" style={{ width: '11%' }} />
            </DataTable>

            <Sidebar header={masterData?._id ? 'Edit LMS' : 'Add LMS'} className='dialog-popup' visible={visible}
                onHide={onCancel} draggable={false} position="right">
                <AddEditLMS onCancel={onCancel} masterData={masterData} schoolId={schoolId} financialYear={financialYear} userId={securityUserID} />
            </Sidebar>

            <Sidebar header={`Submissions${submissionsRow?.Name ? ` — ${submissionsRow.Name}` : ''}`} className='dialog-popup' visible={!!submissionsRow}
                onHide={() => setSubmissionsRow(null)} draggable={false} position="right" style={{ width: '92vw', maxWidth: '1150px' }}>
                {submissionsRow && (
                    <LMSSubmissions lmsId={submissionsRow._id} schoolId={schoolId} financialYear={financialYear} userId={securityUserID} />
                )}
            </Sidebar>

            <Dialog visible={isDeleteConfirmation} onHide={() => setIsDeleteConfirmation(false)}
                header={<span>Delete Confirmation</span>}
                footer={<div className="dialog-footer p-d-flex p-ai-center p-jc-end">
                    <Button label="Cancel" className="btn-text-action-secondary" onClick={() => setIsDeleteConfirmation(false)} />
                    <Button label="Delete" className="btn-text-action-primary" onClick={handleDelete} />
                </div>} draggable={false} dismissableMask position="center" style={{ width: '450px' }}>
                <DeleteConfirmation />
            </Dialog>
        </div>
    );
};

export default LMS;
